import { Product } from '@shared/models/product.model';
import {
  UsfProductCardDesktopHeights,
  UsfProductCardTabletHeights,
  UsfProductCardMobileHeights,
} from '../../shared/models/usf-product-card.model';
import { ProductTypes } from '../../shared/constants/product-type.enum';

export const calculateDesktopProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
  isGuestUser: boolean = false,
): number => {
  if (!product) {
    return null;
  }
  let height = 0;

  if (product?.hasAlternative && !isGuestUser) {
    height += calculateSplitDesktopProductHeightForSearch(product, hasReview);
  } else {
    height += calculateBaseDesktopProductHeightForSearch(
      product,
      hasReview,
      isGuestUser,
    );
  }
  return height;
};

export const calculateBaseDesktopProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
  isGuestUser: boolean = false,
): number => {
  let height = isGuestUser
    ? UsfProductCardDesktopHeights.guestBaseHeight
    : UsfProductCardDesktopHeights.baseHeight;
  if (product?.productTags?.length > 0 && !product?.promotionDetails) {
    height += UsfProductCardDesktopHeights.tagsHeight;
  }
  if (
    product?.productTags?.length === 0 &&
    product?.promotionDetails &&
    !isGuestUser
  ) {
    height += UsfProductCardDesktopHeights.promotionsHeight;
  }
  if (
    product?.drawerMsg?.message &&
    (!isGuestUser ||
      (isGuestUser && product?.drawerMsg?.isDirectProductMessage))
  ) {
    height += UsfProductCardDesktopHeights.drawerHeight;
  }
  if (product?.drawerMsg?.isWarning && !isGuestUser) {
    height += UsfProductCardDesktopHeights.warningBorderHeight;
  }
  if (hasReview) {
    height += UsfProductCardDesktopHeights.ratingsHeight;
  }
  if (product?.showWorksWellWith && !isGuestUser) {
    height += UsfProductCardDesktopHeights.worksWellWithHeight;
  }
  return height;
};

export const calculateSplitDesktopProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
): number => {
  let height = UsfProductCardDesktopHeights.baseSplitHeight;
  if (
    product?.productTags?.length > 0 ||
    product?.alternative?.product?.productTags?.length > 0
  ) {
    height += UsfProductCardDesktopHeights.splitSearchTagsHeight;
  }
  if (
    product?.promotionDetails ||
    (product?.alternative?.product?.promotionDetails &&
      product?.productTags?.length === 0 &&
      product?.alternative?.product?.productTags?.length === 0)
  ) {
    height += UsfProductCardDesktopHeights.splitSearchPromotionsHeight;
  }
  if (product?.drawerMsg?.message) {
    height += UsfProductCardDesktopHeights.drawerHeight;
    if (product?.drawerMsg.isWarning) {
      height += UsfProductCardDesktopHeights.splitWarningBorderHeight;
    }
  }
  if (hasReview) {
    height += UsfProductCardDesktopHeights.ratingsHeight;
  }
  return height;
};

export const calculateTabletProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
  isGuestUser: boolean = false,
): number => {
  if (!product) {
    return null;
  }
  let height = 0;
  if (product?.hasAlternative && !isGuestUser) {
    height += calculateSplitTabletProductHeightForSearch(product, hasReview);
  } else {
    height += calculateBaseTabletProductHeightForSearch(
      product,
      hasReview,
      isGuestUser,
    );
  }
  return height;
};

export const calculateBaseTabletProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
  isGuestUser: boolean = false,
): number => {
  let height = isGuestUser
    ? UsfProductCardTabletHeights.guestBaseHeight
    : UsfProductCardTabletHeights.baseHeight;
  if (product?.customerProductNumber && !isGuestUser) {
    height += UsfProductCardTabletHeights.customerProductNumberHeight;
  }
  if (product?.productTags?.length > 0) {
    height +=
      UsfProductCardTabletHeights.tagsWithLineNumberHeight +
      UsfProductCardTabletHeights.lineNumberHeight;
  }
  if (
    product?.drawerMsg?.message &&
    (!isGuestUser ||
      (isGuestUser && product?.drawerMsg?.isDirectProductMessage))
  ) {
    height += UsfProductCardTabletHeights.drawerHeight;
  }
  if (product?.drawerMsg?.isWarning && !isGuestUser) {
    height += UsfProductCardTabletHeights.warningBorderHeight;
  }
  if (hasReview) {
    height += UsfProductCardTabletHeights.ratingsHeight;
  }
  if (product?.showWorksWellWith && !isGuestUser) {
    height += UsfProductCardTabletHeights.worksWellWithHeight;
  }
  return height;
};

export const calculateSplitTabletProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
): number => {
  let height = UsfProductCardTabletHeights.baseSplitHeight;
  if (product?.customerProductNumber) {
    height += UsfProductCardTabletHeights.splitCustomerProductNumberHeight;
  }
  if (product?.alternative?.product?.productTags?.length > 0) {
    height +=
      UsfProductCardTabletHeights.tagsWithLineNumberHeight +
      UsfProductCardTabletHeights.lineNumberHeight;
  }
  if (product?.drawerMsg?.message) {
    height += UsfProductCardTabletHeights.drawerHeight;
  }
  if (product?.productType === ProductTypes.stat0OutOfStock) {
    height += UsfProductCardTabletHeights.stat0OutOfStockQtyBoxesHeight;
  }
  if (hasReview) {
    height += UsfProductCardTabletHeights.ratingsHeight;
  }
  return height;
};

export const calculateMobileProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
  isGuestUser: boolean = false,
): number => {
  if (!product) {
    return null;
  }
  let height = 0;
  if (product?.hasAlternative && !isGuestUser) {
    height += calculateSplitMobileProductHeightForSearch(product, hasReview);
  } else {
    height += calculateBaseMobileProductHeightForSearch(
      product,
      hasReview,
      isGuestUser,
    );
  }
  return height;
};

export const calculateBaseMobileProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
  isGuestUser: boolean = false,
): number => {
  let height = isGuestUser
    ? UsfProductCardMobileHeights.guestBaseHeight
    : UsfProductCardMobileHeights.baseHeight;
  if (product?.customerProductNumber) {
    height += UsfProductCardMobileHeights.customerProductNumberHeight;
  }
  if (product?.productTags?.length > 0) {
    height += UsfProductCardMobileHeights.alternativeTagsHeight;
  }
  if (
    product?.drawerMsg?.message &&
    (!isGuestUser ||
      (isGuestUser && product?.drawerMsg?.isDirectProductMessage))
  ) {
    height += UsfProductCardMobileHeights.drawerHeight;
  }
  if (product?.drawerMsg?.isWarning && !isGuestUser) {
    height += UsfProductCardMobileHeights.warningBorderHeight;
  }
  if (product?.promotionDetails && !isGuestUser) {
    height += UsfProductCardMobileHeights.promotionsHeight;
  }
  if (hasReview) {
    height += UsfProductCardMobileHeights.ratingsHeight;
  }
  if (product?.showWorksWellWith && !isGuestUser) {
    height += UsfProductCardMobileHeights.worksWellWithHeight;
  }
  return height;
};

export const calculateSplitMobileProductHeightForSearch = (
  product: Product,
  hasReview: boolean = false,
): number => {
  let height = UsfProductCardMobileHeights.baseSplitHeight;
  if (product?.productCardResolutionMsg?.secondaryText) {
    height += UsfProductCardMobileHeights.extendedResolutionMsgHeight;
  }
  if (product?.customerProductNumber) {
    height += UsfProductCardMobileHeights.splitCustomerProductNumberHeight;
  }
  if (product?.alternative?.product?.productTags?.length > 0) {
    height += UsfProductCardMobileHeights.alternativeTagsHeight;
  }
  if (product?.drawerMsg?.message) {
    height += UsfProductCardMobileHeights.drawerHeight;
  }
  if (product?.promotionDetails) {
    height += UsfProductCardMobileHeights.promotionsHeight;
  }
  if (product?.alternative?.product?.promotionDetails) {
    height += UsfProductCardMobileHeights.promotionsHeight;
  }
  if (product?.productType === ProductTypes.stat0OutOfStock) {
    height += UsfProductCardTabletHeights.stat0OutOfStockQtyBoxesHeight;
  }
  if (hasReview) {
    height += UsfProductCardMobileHeights.splitRatingsHeight;
  }
  return height;
};
